// src/contexts/NotificationContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { fetchWithAuth, postWithAuth } from "../utils/fetchWithAuth";

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch notifications from API (limited to 50 most recent)
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchWithAuth("/api/accounts/notifications/?limit=50");

      // Handle both response formats: { notifications: [], unread_count } or just []
      const notificationList = data.notifications || data || [];
      setNotifications(notificationList);
      setUnreadCount(
        data.unread_count ?? notificationList.filter(n => !n.is_read).length
      );
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
      setError(err.message || "Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  // Mark single notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await postWithAuth(`/api/accounts/notifications/${notificationId}/read/`, {});
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await postWithAuth("/api/accounts/notifications/read-all/", {});
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all notifications as read:", err);
    }
  }, []);

  // Fetch on mount and set up polling
  useEffect(() => {
    fetchNotifications();
    
    // Poll every 60 seconds for new notifications
    const interval = setInterval(fetchNotifications, 60000);
    
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Add a new notification locally (for real-time updates via WebSocket)
  const addNotification = useCallback((notification) => {
    setNotifications(prev => [notification, ...prev]);
    if (!notification.is_read) {
      setUnreadCount(prev => prev + 1);
    }
  }, []);

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    addNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

const _emptyNotifications = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
  fetchNotifications: () => {},
  markAsRead: () => {},
  markAllAsRead: () => {},
  addNotification: () => {},
};

export function useNotifications() {
  const context = useContext(NotificationContext);
  // Return safe defaults when rendered outside NotificationProvider (e.g., public routes)
  return context ?? _emptyNotifications;
}

export default NotificationContext;